import { Component, effect, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminButtonComponent,
  AdminModalComponent,
  AdminTextareaComponent,
} from '../../admin';
import { LeadTransitionOption, LeadStatusSummary } from '../../services/leads.service';

@Component({
  selector: 'app-workflow-transition-modal',
  standalone: true,
  imports: [FormsModule, AdminModalComponent, AdminButtonComponent, AdminTextareaComponent],
  template: `
    @if (open()) {
      <app-admin-modal [title]="transition()?.transitionName ?? 'Workflow transition'" (closed)="onClose()">
        @if (transition(); as t) {
          <div class="transition-modal">
            <div class="transition-modal__status">
              <span class="transition-modal__status-badge">{{ currentStatus()?.name ?? '—' }}</span>
              <span class="transition-modal__arrow" aria-hidden="true">→</span>
              <span class="transition-modal__status-badge transition-modal__status-badge--target">
                {{ t.toStatus.name }}
              </span>
            </div>

            @if (t.description?.trim()) {
              <p class="transition-modal__description">{{ t.description }}</p>
            }

            <app-admin-textarea
              class="transition-modal__comment"
              [label]="t.requiresComment ? 'Comment (required)' : 'Comment (optional)'"
              name="transitionComment"
              [(value)]="comment"
              [disabled]="busy()"
              [rows]="4"
              [fullWidth]="true"
              placeholder="Add context for this transition…"
            />

            @if (validationError()) {
              <p class="transition-modal__error" role="alert">{{ validationError() }}</p>
            }
            @if (error()) {
              <p class="transition-modal__error" role="alert">{{ error() }}</p>
            }

            <div class="transition-modal__actions">
              <app-admin-button variant="cancel" type="button" [disabled]="busy()" (click)="onClose()">
                Cancel
              </app-admin-button>
              <app-admin-button variant="primary" type="button" [disabled]="busy()" (click)="onSubmit()">
                {{ busy() ? 'Saving…' : 'Save' }}
              </app-admin-button>
            </div>
          </div>
        }
      </app-admin-modal>
    }
  `,
  styles: `
    .transition-modal { display: flex; flex-direction: column; gap: 1rem; }
    .transition-modal__status {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
    }
    .transition-modal__status-badge {
      display: inline-block;
      padding: 0.25rem 0.625rem;
      border-radius: 9999px;
      background: #f1f5f9;
      color: #334155;
      font-size: 0.875rem;
      font-weight: 600;
    }
    .transition-modal__status-badge--target {
      background: #dbeafe;
      color: #1d4ed8;
    }
    .transition-modal__arrow { color: #64748b; font-weight: 600; }
    .transition-modal__description {
      margin: 0;
      color: #475569;
      line-height: 1.5;
      font-size: 0.9375rem;
    }
    .transition-modal__comment { width: 100%; }
    .transition-modal__error {
      margin: 0;
      color: #b91c1c;
      font-size: 0.875rem;
      font-weight: 500;
    }
    .transition-modal__actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 0.75rem;
    }
  `,
})
export class WorkflowTransitionModalComponent {
  readonly open = input(false);
  readonly transition = input<LeadTransitionOption | null>(null);
  readonly currentStatus = input<LeadStatusSummary | null>(null);
  readonly busy = input(false);
  readonly error = input<string | null>(null);

  readonly confirmed = output<{ comment?: string }>();
  readonly closed = output<void>();

  comment = '';
  readonly validationError = signal<string | null>(null);

  constructor() {
    effect(() => {
      if (this.open()) {
        this.comment = '';
        this.validationError.set(null);
      }
    });
  }

  onClose(): void {
    this.closed.emit();
  }

  onSubmit(): void {
    const t = this.transition();
    if (!t) return;

    const trimmed = this.comment.trim();
    if (t.requiresComment && !trimmed) {
      this.validationError.set('A comment is required for this transition.');
      return;
    }

    this.validationError.set(null);
    this.confirmed.emit({ comment: trimmed || undefined });
  }
}
